import { useEffect, useRef, useState } from "react";
import { Play, Square, Music2 } from "lucide-react";
import { speak } from "@/lib/tts";
import { ALPHABET_SOUNDS } from "@/lib/alphabetData";

// Rhythm groups roughly match the traditional ABC song phrasing.
// Each group is spoken as one utterance; between letters within a group we
// highlight tiles on a paced timer to sync with the audio.
const GROUPS = [
  { letters: ["A", "B", "C", "D"], perLetterMs: 500 },
  { letters: ["E", "F", "G"], perLetterMs: 550 },
  { letters: ["H", "I", "J", "K"], perLetterMs: 500 },
  { letters: ["L", "M", "N", "O", "P"], perLetterMs: 380 }, // rushed L-M-N-O-P
  { letters: ["Q", "R", "S"], perLetterMs: 600 },
  { letters: ["T", "U", "V"], perLetterMs: 600 },
  { letters: ["W", "X"], perLetterMs: 700 },
  { letters: ["Y", "Z"], perLetterMs: 800, prefix: "Y and" },
];

const CLOSING = "Now I know my A-B-Cs. Next time won't you sing with me?";

const LETTERS = ALPHABET_SOUNDS.map((a) => a.letter); // stable A..Z for rendering

export default function ABCSong() {
  const [playing, setPlaying] = useState(false);
  const [activeLetter, setActiveLetter] = useState(null);
  const timersRef = useRef([]);

  const clearTimers = () => {
    timersRef.current.forEach((t) => clearTimeout(t));
    timersRef.current = [];
    if (typeof window !== "undefined" && window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
  };

  useEffect(() => () => clearTimers(), []);

  const stop = () => {
    clearTimers();
    setActiveLetter(null);
    setPlaying(false);
  };

  const sing = () => {
    clearTimers();
    setPlaying(true);
    setActiveLetter(null);

    let cursor = 0;
    GROUPS.forEach((group) => {
      // Schedule per-letter highlight
      group.letters.forEach((letter, i) => {
        const t = setTimeout(() => setActiveLetter(letter), cursor + i * group.perLetterMs);
        timersRef.current.push(t);
      });
      // Schedule the utterance at the start of the group
      const utterance = group.prefix
        ? `${group.prefix} ${group.letters[group.letters.length - 1]}`
        : group.letters.join(" ");
      const startT = setTimeout(() => speak(utterance, { rate: 0.9 }), cursor);
      timersRef.current.push(startT);
      cursor += group.letters.length * group.perLetterMs;
    });

    // Closing phrase
    const closingT = setTimeout(() => {
      setActiveLetter(null);
      speak(CLOSING, { rate: 0.9 });
    }, cursor);
    timersRef.current.push(closingT);

    const endT = setTimeout(() => {
      setActiveLetter(null);
      setPlaying(false);
    }, cursor + 3600);
    timersRef.current.push(endT);
  };

  return (
    <section
      id="abc-song"
      data-testid="abc-song-section"
      className="mt-16"
    >
      <div className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest bg-rose-50 text-rose-700 px-3 py-1 rounded-full mb-3">
        <Music2 className="w-3.5 h-3.5" /> The ABC song
      </div>
      <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">
        Sing the alphabet
      </h2>
      <p className="mt-3 text-slate-600 text-lg max-w-2xl">
        Press play and follow along &mdash; every letter lights up as it&rsquo;s
        sung. A cheerful way to learn the alphabet by ear.
      </p>

      <div className="mt-8 bg-gradient-to-br from-rose-50 via-white to-amber-50 rounded-3xl p-6 sm:p-10 border-2 border-white shadow-[0_10px_40px_rgb(0,0,0,0.04)]">
        <div
          data-testid="abc-song-grid"
          className="grid grid-cols-6 sm:grid-cols-7 md:grid-cols-9 lg:grid-cols-13 gap-2 sm:gap-3"
        >
          {LETTERS.map((letter) => {
            const isActive = activeLetter === letter;
            return (
              <div
                key={letter}
                data-testid={`abc-song-letter-${letter}`}
                className={`aspect-square flex items-center justify-center rounded-2xl border-2 font-display text-2xl sm:text-3xl font-semibold transition-all duration-200
                  ${isActive
                    ? "bg-rose-500 border-rose-600 text-white scale-110 shadow-lg phonx-letter-pop"
                    : "bg-white border-slate-100 text-slate-700"
                  }
                `}
              >
                {letter}
              </div>
            );
          })}
        </div>

        <div className="mt-6 flex justify-center gap-3">
          {!playing ? (
            <button
              data-testid="abc-song-play"
              onClick={sing}
              className="inline-flex items-center gap-2 rounded-full px-8 py-4 bg-rose-500 hover:bg-rose-600 text-white font-bold shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all"
            >
              <Play className="w-5 h-5" fill="currentColor" /> Sing the ABC song
            </button>
          ) : (
            <button
              data-testid="abc-song-stop"
              onClick={stop}
              className="inline-flex items-center gap-2 rounded-full px-8 py-4 bg-slate-800 hover:bg-slate-900 text-white font-bold shadow-md hover:shadow-lg transition-all"
            >
              <Square className="w-5 h-5" fill="currentColor" /> Stop
            </button>
          )}
        </div>
      </div>
    </section>
  );
}
